import React, { useEffect, useState } from "react";
import { Form, Input, Button, Card, Spin, Tabs, Popconfirm } from "antd";
import { Save, Image as ImageIcon, Plus, Trash2 } from "lucide-react";
import {
  useGetCmsConfig,
  useSaveCmsConfig,
} from "../../../hooks/api/cms/hooks";
import {
  IJoinArtParksConfigData,
  IJoinArtParksTab,
} from "../../../types/api/join-art-parks";
import UploadImage from "../../components/UploadImage";
import TinyMce from "../../../utils/TinyMCE";
import { useMessageApi } from "../../../provider/AntdNotification";

const defaultTabs: IJoinArtParksTab[] = [
  { id: "1", title: "Exposure / Audience", description: "" },
  { id: "2", title: "Selling", description: "" },
  { id: "3", title: "Rates/Cost", description: "" },
  { id: "4", title: "Ease of use", description: "" },
];

const JoinArtParksPage: React.FC = () => {
  const [form] = Form.useForm();
  const messageApi = useMessageApi();
  const { data: configData, isLoading } =
    useGetCmsConfig<IJoinArtParksConfigData>("JOIN_ART_PARKS");
  const saveMutation = useSaveCmsConfig<IJoinArtParksConfigData>({
    page: "JOIN_ART_PARKS",
    onSuccess: () => {
      messageApi.success("Join Art Parks configuration saved successfully.");
    },
    onError: (err: any) => {
      messageApi.error(
        err?.response?.data?.message ??
          err?.message ??
          "Failed to save configuration",
      );
    },
  });

  const [tabs, setTabs] = useState<IJoinArtParksTab[]>(defaultTabs);
  const [activeTabKey, setActiveTabKey] = useState<string>("1");

  useEffect(() => {
    if (configData?.data) {
      const data: IJoinArtParksConfigData = configData.data;
      if (data.tabs && Array.isArray(data.tabs) && data.tabs.length > 0) {
        setTabs(data.tabs);
        setActiveTabKey(data.tabs[0].id);
      } else {
        // Fallback for legacy fields format
        const legacyTabs: IJoinArtParksTab[] = [
          {
            id: "1",
            title: "Exposure / Audience",
            description: (data as any).exposureAudience || "",
          },
          {
            id: "2",
            title: "Selling",
            description: (data as any).selling || "",
          },
          {
            id: "3",
            title: "Rates/Cost",
            description: (data as any).ratesCost || "",
          },
          {
            id: "4",
            title: "Ease of use",
            description: (data as any).easeOfUse || "",
          },
        ];
        setTabs(legacyTabs);
        setActiveTabKey("1");
      }
      form.setFieldsValue({
        title: data.title || "",
        subtitle: data.subtitle || "",
        introText: data.introText || (data as any).introText || "",
        image: data.image || (data as any).image || "",
        disclaimer: data.disclaimer || (data as any).disclaimer || "",
      });
    }
  }, [configData, form]);

  const addTab = () => {
    const newId = String(Date.now());
    const newTab: IJoinArtParksTab = {
      id: newId,
      title: `Section ${tabs.length + 1}`,
      description: "",
    };
    const updated = [...tabs, newTab];
    setTabs(updated);
    setActiveTabKey(newId);
  };

  const removeTab = (targetId: string) => {
    if (tabs.length <= 1) return;
    const updated = tabs.filter((t) => t.id !== targetId);
    setTabs(updated);
    if (activeTabKey === targetId) {
      setActiveTabKey(updated[0]?.id || "1");
    }
  };

  const updateTabTitle = (id: string, newTitle: string) => {
    setTabs((prev) =>
      prev.map((tab) => (tab.id === id ? { ...tab, title: newTitle } : tab)),
    );
  };

  const updateTabDescription = (id: string, newDesc: string) => {
    setTabs((prev) =>
      prev.map((tab) =>
        tab.id === id ? { ...tab, description: newDesc } : tab,
      ),
    );
  };

  const onFinish = (values: any) => {
    const payload: IJoinArtParksConfigData = {
      title: values.title || "",
      subtitle: values.subtitle || "",
      introText: values.introText || "",
      image: values.image || "",
      tabs,
      disclaimer: values.disclaimer || "",
    };
    saveMutation.mutate(payload);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64 w-full">
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div className="w-full space-y-6 pb-12">
      <div className="w-full">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-semibold text-slate-800 flex items-center gap-2">
              <ImageIcon className="text-primary" size={24} /> Join Art Parks
              CMS
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              Customize the header intro, banner image, and dynamic
              informational tabs.
            </p>
          </div>
          <Button
            type="dashed"
            icon={<Plus size={16} />}
            onClick={addTab}
            className="flex items-center gap-2"
          >
            Add Info Section Tab
          </Button>
        </div>

        <Form
          form={form}
          layout="vertical"
          onFinish={onFinish}
          className="w-full"
        >
          {/* Header Banner & Intro Section */}
          <Card
            title={
              <span className="font-semibold text-slate-800">Page Header</span>
            }
            className="w-full mb-6 shadow-sm"
          >
            <Form.Item name="title" label="Page Heading">
              <Input placeholder="Enter page heading..." />
            </Form.Item>
            <Form.Item name="subtitle" label="Page Description">
              <Input.TextArea
                rows={4}
                placeholder="Enter page description..."
              />
            </Form.Item>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Editor (Left Side Description)
                </label>
                <Form.Item name="introText" className="mb-0">
                  <TinyMce
                    value={form.getFieldValue("introText")}
                    onChange={(val) => form.setFieldValue("introText", val)}
                  />
                </Form.Item>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Image (Right Side)
                </label>
                <Form.Item name="image" className="mb-0">
                  <UploadImage
                    listType="picture-card"
                    value={form.getFieldValue("image")}
                    onChange={(val) => form.setFieldValue("image", val)}
                  />
                </Form.Item>
              </div>
            </div>
          </Card>

          {/* Dynamic Information Sections Tabs */}
          <Card
            title={
              <span className="font-semibold text-slate-800">
                Dynamic Info Sections
              </span>
            }
            className="w-full mb-6 shadow-sm"
          >
            <Tabs
              activeKey={activeTabKey}
              onChange={setActiveTabKey}
              type="editable-card"
              onEdit={(targetKey, action) => {
                if (action === "add") addTab();
                if (action === "remove" && typeof targetKey === "string")
                  removeTab(targetKey);
              }}
              items={tabs.map((tab) => ({
                key: tab.id,
                label: (
                  <span className="font-medium text-slate-700">
                    {tab.title}
                  </span>
                ),
                children: (
                  <div className="pt-4 flex flex-col gap-4">
                    <div className="flex items-center gap-4">
                      <div className="flex-1">
                        <label className="text-xs font-semibold text-slate-500 mb-1 block">
                          Tab Title
                        </label>
                        <Input
                          value={tab.title}
                          onChange={(e) =>
                            updateTabTitle(tab.id, e.target.value)
                          }
                          placeholder="e.g. Exposure / Audience, Selling, Rates/Cost..."
                          className="max-w-md"
                        />
                      </div>
                      {tabs.length > 1 && (
                        <Popconfirm
                          title="Delete this section tab?"
                          onConfirm={() => removeTab(tab.id)}
                          okText="Yes"
                          cancelText="No"
                        >
                          <Button
                            danger
                            type="text"
                            icon={<Trash2 size={16} />}
                            className="mt-5 flex items-center gap-1"
                          >
                            Remove Section
                          </Button>
                        </Popconfirm>
                      )}
                    </div>

                    <div>
                      <label className="text-xs font-semibold text-slate-500 mb-1 block">
                        Section Description
                      </label>
                      <Input.TextArea
                        rows={6}
                        value={tab.description}
                        onChange={(e) =>
                          updateTabDescription(tab.id, e.target.value)
                        }
                        placeholder="Enter section description..."
                      />
                    </div>
                  </div>
                ),
              }))}
            />
          </Card>
        </Form>
      </div>

      {/* Always Visible Sticky Footer Save Bar */}
      <div className="sticky bottom-4 bg-white/95 backdrop-blur border border-slate-200 py-3 px-6 rounded-xl z-40 flex items-center justify-between shadow-lg">
        <span className="text-sm text-slate-500 font-medium">
          {tabs.length} Section Tab{tabs.length > 1 ? "s" : ""} Configured
        </span>
        <Button
          type="primary"
          size="large"
          icon={<Save size={18} />}
          loading={saveMutation.isPending}
          onClick={() => form.submit()}
          className="!bg-primary flex items-center gap-2 px-8 shadow-md"
        >
          Save Configuration
        </Button>
      </div>
    </div>
  );
};

export default JoinArtParksPage;
